import { Directive, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef, ElementRef, HostListener } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { filter, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';

@Directive({
  selector: '[popoverTrigger]',
  exportAs: 'popoverTrigger',
})
export class PopoverDirective implements OnDestroy, OnInit, OnChanges {
  @Input()
  popoverTrigger!: TemplateRef<object> | null;

  @Input()
  closeOnClickOutside: boolean = false;

  @Input()
  position: ConnectedPosition = {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
  };

  @Input() mouseOnHover: boolean = false;

  @Input() backDrop: boolean = true;

  @Input() width: string;

  unsubscribe = new Subject();
  overlayRef!: OverlayRef;
  state: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private elementRef: ElementRef, private overlay: Overlay, private viewContainerRef: ViewContainerRef) {}

  ngOnInit(): void {
    this.createOverlay();
    this.getState().subscribe((resp) => {
      if (resp) {
        this.detachOverlay();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['position'] && !changes['position'].firstChange && this.overlayRef) {
      const wasAttached = this.overlayRef.hasAttached();
      this.overlayRef.dispose();
      this.createOverlay();
      if (wasAttached) {
        this.open();
      }
    }
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  @HostListener('click') clickou() {
    if (this.popoverTrigger) {
      this.toggle();
    }
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (this.mouseOnHover && !this.overlayRef.hasAttached()) {
      this.open();
    }
  }

  createOverlay(): void {
    const scrollStrategy = this.overlay.scrollStrategies.block();

    const positionStrategy = this.overlay.position().flexibleConnectedTo(this.elementRef).withPositions([this.position]);
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: this.backDrop,
      width: this.width,
      backdropClass: '',
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.closeOnClickOutside) {
          this.detachOverlay();
        }
      });

    // With `backDrop: false` there is no backdrop to click, so `backdropClick`
    // never fires. Only primary clicks count, matching backdropClick.
    this.overlayRef
      .outsidePointerEvents()
      .pipe(
        filter((event) => event.type === 'click'),
        takeUntil(this.unsubscribe)
      )
      .subscribe((event) => {
        if (!this.closeOnClickOutside) {
          return;
        }

        // The host's own click handler already toggles the overlay.
        const target = event.target as HTMLElement | null;
        if (!target || this.elementRef.nativeElement.contains(target)) {
          return;
        }

        // A click inside another overlay pane — a datepicker or dialog opened
        // from this popover's content — is not an outside click. The CDK steps
        // over overlays that do not subscribe to this stream.
        if (target.closest('.cdk-overlay-pane')) {
          return;
        }

        this.detachOverlay();
      });
  }

  open(): void {
    if (this.popoverTrigger) {
      const periodSelectorPortal = new TemplatePortal(this.popoverTrigger, this.viewContainerRef, {
        $implicit: this,
      });
      this.overlayRef.attach(periodSelectorPortal);
    }
  }

  getState(): Observable<boolean> {
    return this.state.asObservable();
  }

  setState(value: boolean) {
    return this.state.next(value);
  }

  close(): void {
    this.overlayRef.detach();
  }

  attachOverlay(): void {
    if (!this.overlayRef.hasAttached()) {
      this.open();
    }
  }

  toggle(): void {
    if (!this.overlayRef.hasAttached()) {
      this.open();
    } else {
      this.close();
    }
  }

  detachOverlay(): void {
    if (this.overlayRef.hasAttached()) {
      this.close();
    }
  }

  updatePosition() {
    setTimeout(() => {
      this.overlayRef.updatePosition();
    }, 100);
  }
}
